import { action, observable, reaction, runInAction } from "mobx";
import { App } from "@capacitor/app";
import { DateTime } from "luxon";
import { BankIdApiService } from "@api-services/integrations/BankIdApiService";
import { LoaderStore } from "@state/stores/common";
import { getRedirectOriginUrl } from "@utils/specific/capacitor/helpers";
import { StorageStateKeysEnum } from "@utils/storage";
import { BankIdSessionStatus } from "@utils/common/bankIdSessionStatus";
import { LoginType } from "@utils/common/loginTypeEnum";
import { BankIdFormData } from "@api-types/integrations";
import { localizationService } from "@state";
import { BankIdApiAuthDto } from "@api-types/integrations/BankIdApiAuthDto";
import { BankIdMvcService } from "@api-services/integrations/BankIdMvcService";
import { BankIdSessionStatusDto } from "@api-types/integrations/BankIdSessionStatusDto";
import { BankIdAuthType } from "@data-types/user/BankIdAuthType";
import { BankIdStorageKey } from "@utils/specific/bankid/bankIdConstants";
import { bankIdRedirectMessageKey } from "@utils/specific/bankid/bankIdRedirect";
import type { RootStore } from "@state/stores/types";

export default class BankIdCapacitorStore {
	@observable isBankIdFlowPopupActive: boolean = false;
	@observable isBankIdQrCodePopupActive: boolean = false;

	@observable capacitorFormData: BankIdFormData | null = null;
	@observable isAuthPending: boolean = false;
	@observable qrCodeImage: any | null = null; // state stores SVG from BE
	@observable remainingSeconds: number = 300;
	@observable isTimerExpired: boolean = false;
	@observable isExtendVisible: boolean = false;
	@observable srErrorAnnouncement: string = "";
	countdownIntervalId: ReturnType<typeof setInterval> | null = null;
	qrRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
	qrImageRefreshIntervalId: ReturnType<typeof setInterval> | null = null;

	qrCodeData: BankIdApiAuthDto | null = null;
	authType: BankIdAuthType | null = null;

	shouldStopSessionPolling: boolean = false;
	shouldStopQrPolling: boolean = false;

	tokenTimeout: number = 300000; // 5 minutes = 300000 miliseconds
	sessionPollingInterval: number = 2000; // 2 seconds = 2000 miliseconds
	qrPollingInterval: number = 1000; // 1 second = 1000 miliseconds
	newOrderTimeout: number = 30000; // 30 seconds = 30000 milliseconds

	rootStore: RootStore;
	loaderStore: LoaderStore;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		this.loaderStore = new LoaderStore();

		reaction(
			() =>
				this.rootStore?.capacitorConnectionStatusStore?.connectionStatus
					?.connected,
			(connected) => {
				if (connected === false && this.isBankIdQrCodePopupActive) {
					this.cancelAuth();
				}
			}
		);
	}

	@action.bound
	setIsAuthPending(isAuthPending: boolean) {
		this.isAuthPending = isAuthPending;
	}

	private getBankIdFailureMessageKey(status?: BankIdSessionStatus): string {
		const isUserCancel =
			status === BankIdSessionStatus.Cancelled ||
			status === BankIdSessionStatus.TimeOut;

		return bankIdRedirectMessageKey(isUserCancel);
	}

	pendingStepUpOnValidated: ((sessionId: string) => void) | null = null;

	@action.bound
	activateFlowPopup(authType?: BankIdAuthType) {
		this.isBankIdFlowPopupActive = true;
		this.togglePollingStates(false);
		if (authType) {
			this.authType = authType;
		}
	}

	@action.bound
	activateStepUp(handler: { onValidated: (sessionId: string) => void }) {
		this.pendingStepUpOnValidated = handler.onValidated;
		this.activateFlowPopup(BankIdAuthType.StepUp);
	}

	@action.bound
	runStepUpValidated(sessionId: string) {
		const onValidated = this.pendingStepUpOnValidated;
		this.onDisposeCapacitor();
		onValidated?.(sessionId);
	}

	@action.bound
	activateQrCodePopup() {
		this.isBankIdQrCodePopupActive = true;
		this.togglePollingStates(false);
		this.remainingSeconds = 300;
		this.isTimerExpired = false;
		this.isExtendVisible = false;
	}

	@action.bound
	deactivateFlowPopup() {
		this.isBankIdFlowPopupActive = false;
	}

	@action.bound
	deactivateQrCodePopup() {
		this.togglePollingStates(true);
		this.onDisposeCapacitor();
	}

	@action.bound
	togglePollingStates(pollingState: boolean) {
		this.shouldStopQrPolling = pollingState;
		this.shouldStopSessionPolling = pollingState;
	}

	@action.bound
	async runSameDeviceFlow() {
		try {
			this.deactivateFlowPopup();
			this.setIsAuthPending(true);
			const apiAuthResponse = await this.getApiAuthentication();

			if (!this.isAuthPending) {
				return;
			}
			if (apiAuthResponse?.autoStartToken) {
				await this.redirectToBankId(apiAuthResponse.autoStartToken);
			} else {
				this.setIsAuthPending(false);
			}
		} catch (e) {
			console.error(e);
			this.setIsAuthPending(false);
		}
	}

	@action.bound
	async runDifferentDeviceFlow() {
		this.deactivateFlowPopup();

		if (
			this.rootStore.capacitorConnectionStatusStore?.connectionStatus
				?.connected === false
		) {
			return;
		}

		this.activateQrCodePopup();
		this.startCountdownTimer();

		try {
			await this.getInitialQrCodeData();

			await this.getRefreshedQrCode();
			this.startQrRefreshLoop();
			this.startQrImageRefreshLoop();

			const pingResponse = await this.pingForSessionStatus();

			if (pingResponse?.status === BankIdSessionStatus.Finished) {
				this.srErrorAnnouncement = localizationService.t(
					"login:BANK_ID.AUTH_SUCCESS"
				);
				setTimeout(() => {
					runInAction(() => {
						this.srErrorAnnouncement = "";
					});
				}, 3000);

				if (this.authType == BankIdAuthType.Login) {
					this.rootStore.userAuthStore.loginStore.handleLoginSubmit(
						{ sessionId: pingResponse.sessionId },
						LoginType.BANKID
					);
					this.onDisposeCapacitor();
				}

				if (this.authType == BankIdAuthType.Register) {
					this.assignRegisterData(pingResponse.sessionId);
					this.onDisposeCapacitor();
				}

				if (this.authType == BankIdAuthType.StepUp) {
					this.runStepUpValidated(pingResponse.sessionId);
				}
			} else if (
				pingResponse?.status === BankIdSessionStatus.Failed ||
				pingResponse?.status === BankIdSessionStatus.Cancelled ||
				pingResponse?.status === BankIdSessionStatus.TimeOut
			) {
				const errorMsg = localizationService.t(
					this.getBankIdFailureMessageKey(pingResponse?.status)
				);
				this.onDisposeCapacitor();
				this.rootStore.notificationStore.error(errorMsg);

				setTimeout(() => {
					runInAction(() => {
						this.srErrorAnnouncement = errorMsg;
					});
					setTimeout(() => {
						runInAction(() => {
							this.srErrorAnnouncement = "";
						});
					}, 3000);
				}, 200);
			}
		} catch (error) {
			console.error(error);
			this.rootStore.notificationStore.error(
				localizationService.t(
					"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID"
				)
			);
		}
	}

	@action.bound
	async getApiAuthentication() {
		this.loaderStore.suspend();
		const successRedirectUrl = getRedirectOriginUrl(
			window.location.pathname,
			true
		);
		const failRedirectUrl = getRedirectOriginUrl(
			window.location.pathname + "?failedAuth=true",
			true
		);
		try {
			const response = await BankIdApiService.bankIdApiAuth(
				successRedirectUrl,
				failRedirectUrl
			);

			if (response?.requestId) {
				this.addTokenToLocalStorage(response.requestId);
				this.setTokenTimeout();
				return response;
			}

			return {} as BankIdApiAuthDto;
		} catch (e) {
			console.error(e);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async getInitialQrCodeData(silent = false, updateRequestId = true) {
		if (!silent) this.loaderStore.suspend();
		try {
			const qrCodeData = await BankIdMvcService.getInitialQrCodeData();

			if (!qrCodeData) {
				console.error("No QR code data fetched");
				return;
			}

			if (qrCodeData.requestId && updateRequestId) {
				this.addTokenToLocalStorage(qrCodeData.requestId);
			}

			this.qrCodeData = qrCodeData;
			return;
		} catch (error) {
			console.error(error);
		} finally {
			if (!silent) this.loaderStore.resume();
		}
	}

	@action.bound
	async getRefreshedQrCode() {
		try {
			if (!this.qrCodeData?.qrId) {
				console.error("No QR id inside QR code data");
				return;
			}

			const qrCodeImage = await BankIdMvcService.getRefreshedQrCode(
				this.qrCodeData.qrId
			);

			if (qrCodeImage?.ok) {
				this.qrCodeImage = await qrCodeImage.text();
			}
		} catch (e) {
			console.error("Error fetching QR code:", e);
		}
	}

	@action.bound
	async redirectToBankId(autoStartToken: string) {
		const redirectUrl = `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
		window.location.href = redirectUrl;

		App.addListener("appStateChange", async (state) => {
			if (state.isActive) {
				const requestId = this.getRequestIdFromLocalStorage();

				if (!requestId) {
					return;
				}

				this.setIsAuthPending(true);
				try {
					const pingResponse = await this.pingForSessionStatus();

					if (pingResponse?.status === BankIdSessionStatus.Finished) {
						if (this.authType == BankIdAuthType.Login) {
							this.rootStore.userAuthStore.loginStore.handleLoginSubmit(
								{ sessionId: pingResponse.sessionId },
								LoginType.BANKID
							);
							this.onDisposeCapacitor();
						}

						if (this.authType == BankIdAuthType.Register) {
							this.assignRegisterData(pingResponse.sessionId);
							this.onDisposeCapacitor();
						}

						if (this.authType == BankIdAuthType.StepUp) {
							this.runStepUpValidated(pingResponse.sessionId);
						}
					} else {
						this.onDisposeCapacitor();
						this.rootStore.notificationStore.error(
							localizationService.t(
								this.getBankIdFailureMessageKey(
									pingResponse?.status
								)
							)
						);
					}
				} catch (error) {
					console.error(error);
					this.rootStore.notificationStore.error(
						localizationService.t(
							"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID"
						)
					);
					this.setIsAuthPending(false);
				} finally {
					this.setIsAuthPending(false);
				}
			}
		});
	}

	@action.bound
	async assignRegisterData(sessionId: string) {
		try {
			this.loaderStore.suspend();

			const response = await BankIdApiService.getUserAdditionalInfo({
				sessionId,
			});

			let formData: BankIdFormData = {};

			if (response.firstName && response.lastName) {
				formData.firstName = response.firstName;
				formData.lastName = response.lastName;
			}

			if (response.pinNumber) {
				formData.personalIdentificator = response.pinNumber;
			}

			if (response.birthDate) {
				const datetime = DateTime.fromISO(response.birthDate);

				formData.day = datetime.get("day").toString();
				formData.month = datetime.get("month").toString();
				formData.year = datetime.get("year").toString();
			}
			runInAction(() => {
				this.capacitorFormData = formData;

				sessionStorage.setItem(
					BankIdStorageKey.AdditionalUserInfo,
					JSON.stringify(formData)
				);

				sessionStorage.setItem(
					BankIdStorageKey.PrefilledFieldsDisabled,
					JSON.stringify(true)
				);

				sessionStorage.setItem(
					BankIdStorageKey.RegistrationSessionId,
					sessionId
				);

				console.log("prefilled session state set successfully!");
			});
		} catch (err) {
			console.error(err);
			let errorMessage: string;
			switch (err.data.errorCode) {
				case 400921:
					errorMessage =
						"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID";
					break;
				case 400922:
					errorMessage =
						"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_FAILED";
					break;
				default:
					errorMessage = "registration:GENERIC_ERROR";
					break;
			}
			this.rootStore.notificationStore.error(
				localizationService.t(errorMessage)
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async pingForSessionStatus(): Promise<BankIdSessionStatusDto> {
		return new Promise<BankIdSessionStatusDto>(async (resolve) => {
			const checkStatus = async () => {
				if (this.shouldStopSessionPolling) return;

				try {
					const requestId = this.getRequestIdFromLocalStorage();

					if (!requestId) {
						console.error("No request id inside local storage!");

						this.togglePollingStates(true);
						resolve({ id: "", sessionId: "", status: 0 });
						return;
					}

					const isLogin =
						this.authType == BankIdAuthType.Login ? true : false;

					const response = await BankIdApiService.getSessionStatus(
						requestId,
						isLogin
					);

					if (response.status === BankIdSessionStatus.Finished) {
						this.togglePollingStates(true);
						resolve({
							id: response.id,
							sessionId: response.sessionId,
							status: BankIdSessionStatus.Finished,
						});
						return;
					}

					if (
						response.status === BankIdSessionStatus.Cancelled ||
						response.status === BankIdSessionStatus.TimeOut
					) {
						this.togglePollingStates(true);
						resolve({
							id: response.id,
							sessionId: response.sessionId,
							status: response.status,
						});
						return;
					}

					if (response.status === BankIdSessionStatus.Invalid) {
						// QR flow: the 30-second BankID order expired — expected behavior.
						// Switch to the latest order's requestId and continue polling.
						// Same-device flow: treat as auth failure — stop.
						if (this.isBankIdQrCodePopupActive) {
							if (this.qrCodeData?.requestId) {
								this.addTokenToLocalStorage(
									this.qrCodeData.requestId
								);
							}
							if (!this.shouldStopSessionPolling) {
								setTimeout(
									checkStatus,
									this.sessionPollingInterval
								);
							}
							return;
						}
						this.togglePollingStates(true);
						resolve({
							id: response.id,
							sessionId: response.sessionId,
							status: response.status,
						});
						return;
					}

					if (response.status === BankIdSessionStatus.Failed) {
						// QR flow: treat Failed the same as Invalid — the BankID order expired.
						// Switch to the latest order and continue polling.
						// The countdown timer and cancel button handle stopping.
						if (this.isBankIdQrCodePopupActive) {
							if (this.qrCodeData?.requestId) {
								this.addTokenToLocalStorage(
									this.qrCodeData.requestId
								);
							}
							if (!this.shouldStopSessionPolling) {
								setTimeout(
									checkStatus,
									this.sessionPollingInterval
								);
							}
							return;
						}
						// Same-device flow: auth genuinely failed — stop.
						this.togglePollingStates(true);
						resolve({
							id: response.id,
							sessionId: response.sessionId,
							status: response.status,
						});
						return;
					}

					if (!this.shouldStopSessionPolling) {
						setTimeout(checkStatus, this.sessionPollingInterval);
					}
				} catch (e) {
					console.error("Error while checking session status:", e);

					if (!this.shouldStopSessionPolling) {
						setTimeout(checkStatus, this.sessionPollingInterval);
					}
				}
			};

			await checkStatus();
		});
	}

	@action.bound
	addTokenToLocalStorage(requestId: string) {
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.REQUEST_ID,
			requestId
		);
	}

	@action.bound
	getRequestIdFromLocalStorage(): string {
		const requestId = JSON.stringify(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.REQUEST_ID
			)
		);

		if (!requestId) {
			console.error("There is no requestId inside local storage!");
		}

		return JSON.parse(requestId);
	}

	@action.bound
	removePrefilledStateFromSessionStorage() {
		sessionStorage.removeItem(BankIdStorageKey.PrefilledFieldsDisabled);
		sessionStorage.removeItem(BankIdStorageKey.AdditionalUserInfo);
	}

	@action.bound
	cancelAuth() {
		this.onDisposeCapacitor();
		this.deactivateFlowPopup();
		this.deactivateQrCodePopup();
		this.setIsAuthPending(false);
		this.loaderStore.resume();
	}

	@action.bound
	startCountdownTimer() {
		if (this.countdownIntervalId !== null) {
			clearInterval(this.countdownIntervalId);
		}
		this.remainingSeconds = 300;
		this.isTimerExpired = false;
		this.isExtendVisible = false;

		this.countdownIntervalId = setInterval(() => {
			runInAction(() => {
				this.remainingSeconds -= 1;
				const s = this.remainingSeconds;

				if (s <= 30 && s > 0) {
					this.isExtendVisible = true;
				}

				if (s <= 0) {
					clearInterval(this.countdownIntervalId!);
					this.countdownIntervalId = null;
					this.isTimerExpired = true;
					this.isExtendVisible = false;
					this.shouldStopSessionPolling = true;
				}
			});
		}, 1000);
	}

	@action.bound
	async extendSession() {
		this.stopQrRefreshLoop();
		this.stopQrImageRefreshLoop();
		this.remainingSeconds = 300;
		this.isExtendVisible = false;
		// Fetch a new BankID order for QR display — don't overwrite the polling requestId
		// in case the user already scanned the previous QR. The Invalid/Failed handlers
		// will switch to the new requestId if the old order expired.
		await this.getInitialQrCodeData(true, false);
		await this.getRefreshedQrCode();
		this.startCountdownTimer();
		this.startQrRefreshLoop();
		this.startQrImageRefreshLoop();
	}

	@action.bound
	startQrRefreshLoop() {
		if (this.qrRefreshIntervalId !== null) {
			clearInterval(this.qrRefreshIntervalId);
		}
		this.qrRefreshIntervalId = setInterval(async () => {
			if (this.isTimerExpired || !this.isBankIdQrCodePopupActive) return;
			await this.getInitialQrCodeData(true, false);
			await this.getRefreshedQrCode();
		}, this.newOrderTimeout);
	}

	@action.bound
	stopQrRefreshLoop() {
		if (this.qrRefreshIntervalId !== null) {
			clearInterval(this.qrRefreshIntervalId);
			this.qrRefreshIntervalId = null;
		}
	}

	@action.bound
	startQrImageRefreshLoop() {
		if (this.qrImageRefreshIntervalId !== null) {
			clearInterval(this.qrImageRefreshIntervalId);
		}
		this.qrImageRefreshIntervalId = setInterval(async () => {
			if (this.isTimerExpired || !this.isBankIdQrCodePopupActive) return;
			await this.getRefreshedQrCode();
		}, this.qrPollingInterval);
	}

	@action.bound
	stopQrImageRefreshLoop() {
		if (this.qrImageRefreshIntervalId !== null) {
			clearInterval(this.qrImageRefreshIntervalId);
			this.qrImageRefreshIntervalId = null;
		}
	}

	@action.bound
	async tryAgain() {
		this.qrCodeImage = null;
		await this.runDifferentDeviceFlow();
	}

	@action.bound
	setTokenTimeout() {
		setTimeout(() => {
			this.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.REQUEST_ID
			);
		}, this.tokenTimeout);
	}

	@action.bound
	onDisposeCapacitor() {
		if (this.countdownIntervalId !== null) {
			clearInterval(this.countdownIntervalId);
			this.countdownIntervalId = null;
		}
		this.stopQrRefreshLoop();
		this.stopQrImageRefreshLoop();
		this.remainingSeconds = 300;
		this.isTimerExpired = false;
		this.isExtendVisible = false;
		this.authType = null;
		this.pendingStepUpOnValidated = null;
		this.isBankIdFlowPopupActive = false;
		this.isBankIdQrCodePopupActive = false;
		this.capacitorFormData = null;
		this.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.REQUEST_ID
		);
		this.qrCodeData = null;
		this.qrCodeImage = null;
		this.setIsAuthPending(false);

		sessionStorage.removeItem(BankIdStorageKey.PrefilledFieldsDisabled);
		sessionStorage.removeItem(BankIdStorageKey.AdditionalUserInfo);
	}
}
